import { Link, useNavigate } from 'react-router-dom';

interface CustomColors {
  heading: string;
  body: string;
  link: string;
  linkHover: string;
  muted: string;
  border: string;
  subtle: string;
  subtleHover: string;
}

interface SiteFooterProps {
  gradient?: string;
  darkText?: boolean;
  customColors?: CustomColors;
}

export default function SiteFooter({ gradient, darkText, customColors }: SiteFooterProps) {
  const navigate = useNavigate();

  const handleNavClick = (sectionId: string) => {
    try {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } catch (err) {
      console.error('Navigation error:', err);
    }
  };

  const bg = gradient || 'linear-gradient(135deg, #14b8a6 0%, #10b981 50%, #059669 100%)';

  // If customColors provided, use them; otherwise fall back to tailwind classes
  const useCustom = !!customColors;

  const headingClass = useCustom ? '' : (darkText ? 'text-teal-800' : 'text-white');
  const bodyClass = useCustom ? '' : (darkText ? 'text-teal-700' : 'text-white/90');
  const linkClass = useCustom ? '' : (darkText ? 'text-teal-600 hover:text-teal-900' : 'text-white/80 hover:text-white');
  const mutedClass = useCustom ? '' : (darkText ? 'text-teal-500' : 'text-white/70');
  const borderClass = useCustom ? '' : (darkText ? 'border-teal-200' : 'border-white/20');
  const subtleClass = useCustom ? '' : (darkText ? 'text-teal-400 hover:text-teal-600' : 'text-white/50 hover:text-white/70');

  const headingStyle = useCustom ? { color: customColors!.heading } : {};
  const bodyStyle = useCustom ? { color: customColors!.body } : {};
  const linkStyle = useCustom ? { color: customColors!.link } : {};
  const mutedStyle = useCustom ? { color: customColors!.muted } : {};
  const borderStyle = useCustom ? { borderColor: customColors!.border } : {};
  const subtleStyle = useCustom ? { color: customColors!.subtle } : {};

  return (
    <footer className="py-16" style={{ background: bg }}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/06f7ee287cfacc7a314e7ca2f415ab12.png"
                alt="seitenschmied"
                className="h-10 w-auto"
              />
            </div>
            <p className={`text-base leading-relaxed mb-4 ${bodyClass}`} style={bodyStyle}>
              Ihre Website. Ihr Look. Ihr Erfolg.
            </p>
            <div className={`text-sm space-y-1 ${linkClass}`} style={linkStyle}>
              <p className={`font-semibold ${headingClass}`} style={headingStyle}>Seitenschmied</p>
              <p>Webdesign im Würmtal</p>
              <p>82166 Gräfelfing</p>
            </div>
          </div>

          {/* Seitenschmied */}
          <div>
            <h4 className={`font-bold text-lg mb-4 ${headingClass}`} style={headingStyle}>Seitenschmied</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/ueber-uns"
                  className={`text-sm transition-colors duration-300 ${linkClass}`}
                  style={linkStyle}
                  onMouseEnter={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.linkHover; } : undefined}
                  onMouseLeave={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.link; } : undefined}
                >
                  Über uns
                </Link>
              </li>
              <li>
                <Link
                  to="/faq"
                  className={`text-sm transition-colors duration-300 ${linkClass}`}
                  style={linkStyle}
                  onMouseEnter={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.linkHover; } : undefined}
                  onMouseLeave={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.link; } : undefined}
                >
                  FAQ
                </Link>
              </li>
              <li>
                <button
                  onClick={() => handleNavClick('kontakt')}
                  className={`text-sm transition-colors duration-300 text-left cursor-pointer ${linkClass}`}
                  style={linkStyle}
                  onMouseEnter={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.linkHover; } : undefined}
                  onMouseLeave={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.link; } : undefined}
                >
                  Kontakt
                </button>
              </li>
            </ul>
          </div>

          {/* Rechtliches */}
          <div>
            <h4 className={`font-bold text-lg mb-4 ${headingClass}`} style={headingStyle}>Rechtliches</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/impressum"
                  className={`text-sm transition-colors duration-300 ${linkClass}`}
                  style={linkStyle}
                  onMouseEnter={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.linkHover; } : undefined}
                  onMouseLeave={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.link; } : undefined}
                >
                  Impressum
                </Link>
              </li>
              <li>
                <Link
                  to="/datenschutz"
                  className={`text-sm transition-colors duration-300 ${linkClass}`}
                  style={linkStyle}
                  onMouseEnter={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.linkHover; } : undefined}
                  onMouseLeave={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.link; } : undefined}
                >
                  Datenschutz
                </Link>
              </li>
              <li>
                <Link
                  to="/agb"
                  className={`text-sm transition-colors duration-300 ${linkClass}`}
                  style={linkStyle}
                  onMouseEnter={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.linkHover; } : undefined}
                  onMouseLeave={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.link; } : undefined}
                >
                  AGB
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t" style={{ borderColor: useCustom ? customColors!.border : undefined }}>
          <div>
            <button
              onClick={() => {
                try {
                  localStorage.removeItem('cookie-consent');
                  window.location.reload();
                } catch (err) {
                  console.error('Failed to reset cookie consent:', err);
                }
              }}
              className={`text-sm transition-colors duration-300 flex items-center gap-1.5 cursor-pointer ${linkClass}`}
              style={linkStyle}
              onMouseEnter={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.linkHover; } : undefined}
              onMouseLeave={useCustom ? (e) => { (e.target as HTMLElement).style.color = customColors!.link; } : undefined}
            >
              <i className="ri-settings-3-line"></i>
              Cookie-Einstellungen
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
